import { useState } from "react";
import {
  Box, CardBody, Flex, Grid,
  HStack, IconButton, Image, Text, VStack,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router";
import { LuMail, LuMessageSquare } from "react-icons/lu";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import AppPage from "layouts/AppPage";
import { AssigneeTaskDrawer } from "features/NewHirePortal/Components";
import MeetingRow from "features/NewHirePortal/Components/MeetingRow";
import PortalDisabledState from "features/NewHirePortal/Components/PortalDisabledState";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";
import JourneyStageList from "features/NewHirePortal/Components/JourneyStageList";
import PeoplePills from "features/NewHirePortal/Components/PeoplePills";
import { calculateDaysSinceStart, calculatePercentage, getOnboardingPeriodDisplay } from "features/NewHireJourney/helpers";
import { newHireJourneyStageInfo } from "features/Stage/helpers";
import useCaminoStore from "stores/caminoStore";
import { useCurrentCompanyQuery } from "features/Company/hooks";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useNewHireJourneyTasksQuery } from "features/Resources/Task/hooks";
import { includeTaskInCompletionRate, filterMyJourneyTasks } from "features/Resources/Task/helpers";
import { useNewHireJourneyMeetingsQuery } from "features/Resources/Meeting/hooks";
import { Meeting } from "features/Resources/Meeting/models";
import MeetingDetailDrawer from "features/Resources/Meeting/Components/MeetingDetailDrawer";
import { TaskCard, TaskListHeader } from "features/Resources/Task/Components/UserTaskSummary";
import { Task } from "features/Resources/Task/models";
import RelationNameHeader from "components/RelationNameHeader";
import PersonDrawer, { SupportPerson } from "components/PersonDrawer";
import { RelationType } from "models/v2/networkRelation";
import TransparentCard from "components/Card/TransparentCard";
import CardFooterLink from "components/Card/CardFooterLink";
import BeachDaySVG from "assets/undraw_beach_day.svg";
import LoadingDots from "components/Loading";
import { Heading22 } from "components/Heading";
import SectionHeader from "components/SectionHeader";
import DashboardCard from "pages/PeopleTeam/DashboardPage/DashboardCard";

const MAX_VISIBLE_TASKS = 3;
const MAX_VISIBLE_MEETINGS = 5;
// Threshold for "Behind" status — 3+ overdue tasks signals the new hire needs significant help
const BEHIND_OVERDUE_THRESHOLD = 3;
const TASK_STATUS = {
  behind:         { label: "Behind",          color: "#A32D2D" },
  needsAttention: { label: "Needs attention", color: "#BA7517" },
  onTrack:        { label: "On track",        color: "#1D9E75" },
} as const;

export default function NewHirePortalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useCaminoStore((state) => state.currentUser);
  const credentialType = useCaminoStore((state) => state.credentialType);
  const { data: company } = useCurrentCompanyQuery();
  const { data: journey, isFetched } = useNewHireJourneyDetailQuery(currentUser?.newHireJourneyId);
  const { data: tasks = [] } = useNewHireJourneyTasksQuery(journey?.id || "", { enabled: !!journey?.id });
  const { data: meetings = [] } = useNewHireJourneyMeetingsQuery(journey?.id || "", { enabled: !!journey?.id });
  const [selectedPerson, setSelectedPerson] = useState<SupportPerson | null>(null);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);

  if (
    credentialType === "personal_email"
    && company
    && !company.personalEmailPortalAccessEnabled
  ) {
    return <PortalDisabledState />;
  }

  if (isFetched && !journey) {
    return (
      <AppPage>
        <TransparentCard>
          <CardBody>
            <VStack spacing={6} align="center" textAlign="center" py={8}>
              <Image mx={6} maxWidth="320px" maxHeight="320px" src={BeachDaySVG} />
              <VStack spacing={2}>
                <Text fontSize="2xl" fontWeight="semibold" color="content.primary">
                  No Active Onboarding Journey
                </Text>
                <Text fontSize="md" color="content.tertiary">
                  Sorry, no current onboarding journey found. If this is a mistake, reach out to your People Team!
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </TransparentCard>
      </AppPage>
    );
  }

  if (!journey) {
    return (
      <AppPage>
        <LoadingDots />
      </AppPage>
    );
  }

  const daysCompleted = journey.startDate ? calculateDaysSinceStart(journey.startDate) : 0;
  const periodDisplay = getOnboardingPeriodDisplay(daysCompleted);
  const stageInfo = newHireJourneyStageInfo(journey);
  const earlyStageKeys = ["week1", "month1", "month2", "month3"];
  const welcomeSubtitle = journey.startDate
    ? `${periodDisplay.timeLabel}${earlyStageKeys.includes(stageInfo.key) ? ` — ${stageInfo.label}` : ""}`
    : new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  const myTasks = filterMyJourneyTasks(tasks, currentUser?.id, journey?.id);
  const nonUpcomingTasks = myTasks.filter((t) => t.status !== "upcoming");
  const completedTasksCount = nonUpcomingTasks.filter((t) => t.status === "completed").length;
  const totalTasksCount = nonUpcomingTasks.filter(includeTaskInCompletionRate).length;
  const taskCompletionPercentage = calculatePercentage(completedTasksCount, totalTasksCount);
  const overdueCount = nonUpcomingTasks.filter((t) => t.isOverdue).length;
  const taskStatus = overdueCount >= BEHIND_OVERDUE_THRESHOLD
    ? TASK_STATUS.behind
    : overdueCount >= 1
      ? TASK_STATUS.needsAttention
      : TASK_STATUS.onTrack;

  const actionableTasks = nonUpcomingTasks
    .filter((t) => ["active", "in_progress", "blocked"].includes(t.status))
    .sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      const aDate = a.dueOn ? new Date(a.dueOn).getTime() : Infinity;
      const bDate = b.dueOn ? new Date(b.dueOn).getTime() : Infinity;
      return aDate - bDate;
    });

  const visibleTasks = actionableTasks.slice(0, MAX_VISIBLE_TASKS);
  const remainingTaskCount = actionableTasks.length - MAX_VISIBLE_TASKS;

  const now = new Date();
  const upcomingMeetings = meetings
    .filter((m) => m.status === "scheduled" && m.startTime && new Date(m.startTime) >= now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const meetingSubtitle = upcomingMeetings.length > 0 ? `Showing ${upcomingMeetings.length} Scheduled` : undefined;
  const managerName = journey.manager?.givenName || journey.manager?.firstName;
  const meetingEmptyText = managerName
    ? `No upcoming meetings yet. Check in with ${managerName}!`
    : "No upcoming meetings scheduled";

  const handleTaskClick = (task: Task) => {
    setSearchParams({ task_id: task.id });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
  };

  const selectedTaskId = searchParams.get("task_id");

  const supportCrew = [
    journey.manager && { user: journey.manager, roles: ["manager"] as RelationType[] },
    journey.buddy && { user: journey.buddy, roles: ["buddy"] as RelationType[] },
    journey.peopleTeamContact && { user: journey.peopleTeamContact, roles: ["people_team_contact"] as RelationType[] },
  ].filter(Boolean) as SupportPerson[];

  return (
    <AppPage>
      <VStack spacing={6} align="stretch" pb={8}>
        {/* Welcome Header */}
        <Box>
          <Heading22>{`Welcome${currentUser?.firstName ? `, ${currentUser.firstName}` : "!"} 🌱`}</Heading22>
          <Text fontSize="md" color="content.tertiary" mt={1}>
            {welcomeSubtitle}
          </Text>
        </Box>

        {/* People pills — mobile only */}
        {supportCrew.length > 0 && (
          <Box display={{ base: "block", md: "none" }}>
            <PeoplePills supportCrew={supportCrew} />
          </Box>
        )}

        {/* Main two-column layout */}
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} alignItems="flex-start">
          {/* Left: Tasks + Meetings + mobile journey one-liner */}
          <VStack spacing={4} align="stretch">
            {/* My Tasks */}
            <DashboardCard padding="none">
              {/* Header */}
              <Flex
                justify="space-between"
                align="center"
                px={5}
                pt={4}
                pb={4}
                borderBottomWidth="0.5px"
                borderColor="gray.200"
              >
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="content.primary">My Tasks</Text>
                  <Text fontSize="xs" color="content.tertiary" mt="2px">
                    {totalTasksCount === 0
                      ? "No open tasks"
                      : `${completedTasksCount} of ${totalTasksCount} done${overdueCount > 0 ? ` · ${overdueCount} overdue` : ""}`}
                  </Text>
                </Box>
                {totalTasksCount > 0 && (
                  <Box textAlign="right">
                    <Text fontSize="lg" fontWeight="500" color="content.primary" lineHeight="1">
                      {Math.round(taskCompletionPercentage)}%
                    </Text>
                    <Flex align="center" gap={1} justify="flex-end" mt="3px">
                      <Box w="5px" h="5px" borderRadius="full" bg={taskStatus.color} flexShrink={0} />
                      <Text fontSize="xs" fontWeight="500" color={taskStatus.color}>
                        {taskStatus.label}
                      </Text>
                    </Flex>
                  </Box>
                )}
              </Flex>

              {/* Body */}
              {totalTasksCount === 0 ? (
                <Text fontSize="sm" color="content.tertiary" textAlign="center" px={5} py={6}>
                  You&apos;re all caught up. New tasks appear here when assigned.
                </Text>
              ) : visibleTasks.length === 0 ? (
                <Box>
                  <VStack spacing={1} py={6} px={5} textAlign="center">
                    <Text fontSize="2xl">🎉</Text>
                    <Text fontSize="sm" fontWeight="600" color="content.primary">
                      All tasks complete!
                    </Text>
                    <Text fontSize="xs" color="content.tertiary">
                      Great work — you&apos;re all caught up.
                    </Text>
                  </VStack>
                  <CardFooterLink to={SHARED_ROUTES.newHireTasks}>See all tasks →</CardFooterLink>
                </Box>
              ) : (
                <Box>
                  <TaskListHeader showJourney={false} showAssignee={false} />
                  {visibleTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      compact
                      onClick={handleTaskClick}
                      showAssignee={false}
                      showJourney={false}
                    />
                  ))}
                  <CardFooterLink to={SHARED_ROUTES.newHireTasks} showBorder={remainingTaskCount > 0}>
                    {remainingTaskCount > 0
                      ? `+${remainingTaskCount} more task${remainingTaskCount !== 1 ? "s" : ""} — view all`
                      : "See all tasks →"}
                  </CardFooterLink>
                </Box>
              )}
            </DashboardCard>

            {/* Upcoming Meetings */}
            <DashboardCard>
              <SectionHeader
                title="Upcoming Meetings"
                subtitle={meetingSubtitle}
                linkTo={SHARED_ROUTES.newHireSchedule}
                linkLabel="View schedule →"
              />
              {upcomingMeetings.length === 0 ? (
                <Text fontSize="sm" color="content.tertiary" py="4" textAlign="center">
                  {meetingEmptyText}
                </Text>
              ) : (
                <Box>
                  {upcomingMeetings.slice(0, MAX_VISIBLE_MEETINGS).map((meeting) => (
                    <Box
                      key={meeting.id}
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      onClick={() => setSelectedMeeting(meeting)}
                    >
                      <MeetingRow meeting={meeting} />
                    </Box>
                  ))}
                  {upcomingMeetings.length > MAX_VISIBLE_MEETINGS && (
                    <CardFooterLink to={SHARED_ROUTES.newHireSchedule}>
                      {`+${upcomingMeetings.length - MAX_VISIBLE_MEETINGS} more — view full schedule`}
                    </CardFooterLink>
                  )}
                </Box>
              )}
            </DashboardCard>

            {/* Journey one-liner — mobile only */}
            {journey.startDate && (
              <Box display={{ base: "block", md: "none" }}>
                <Text fontSize="sm" color="content.tertiary" textAlign="center">
                  {periodDisplay.timeLabel} · {stageInfo.label}
                </Text>
              </Box>
            )}
          </VStack>

          {/* Right rail — hidden on small mobile */}
          <VStack spacing={4} align="stretch" display={{ base: "none", md: "flex" }}>
            <JourneyStageList journey={journey} />
            <DashboardCard>
              <SectionHeader title="Your People" />
              <VStack
                spacing={0}
                align="stretch"
                divider={<Box borderBottomWidth="1px" borderColor="gray.100" />}
                mt={2}
              >
                {supportCrew.length === 0 ? (
                  <Text fontSize="sm" color="content.tertiary" py="4" textAlign="center">
                    No support crew assigned yet
                  </Text>
                ) : (
                  supportCrew.map(({ user, roles }) => (
                    <HStack
                      key={user.id}
                      justify="space-between"
                      align="center"
                      py={3}
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      mx={-3}
                      px={3}
                      borderRadius="md"
                      onClick={() => setSelectedPerson({ user, roles })}
                    >
                      <RelationNameHeader user={user} roles={roles} size="sm" />
                      {currentUser?.workspaceAccountActive && user.slackUrl ? (
                        <IconButton
                          aria-label={`Message ${user.fullName} on Slack`}
                          icon={<LuMessageSquare size={18} />}
                          variant="ghost"
                          color="brand.500"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); window.open(user.slackUrl, "_blank"); }}
                          _hover={{ bg: "gray.100" }}
                        />
                      ) : user.workEmail ? (
                        <IconButton
                          aria-label={`Email ${user.fullName}`}
                          icon={<LuMail size={18} />}
                          variant="ghost"
                          color="brand.500"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${user.workEmail}`; }}
                          _hover={{ bg: "gray.100" }}
                        />
                      ) : null}
                    </HStack>
                  ))
                )}
              </VStack>
            </DashboardCard>
          </VStack>
        </Grid>
      </VStack>

      <AssigneeTaskDrawer isOpen={!!selectedTaskId} onClose={handleCloseDrawer} taskId={selectedTaskId} />
      <TaskActionModalByQueryParam />
      <PersonDrawer person={selectedPerson} onClose={() => setSelectedPerson(null)} />
      <MeetingDetailDrawer meeting={selectedMeeting} onClose={() => setSelectedMeeting(null)} showEditLink={false} />
    </AppPage>
  );
}
